import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { littlebirdSdk } from '@/lib/sdk-init';
import { formatSyncSection } from '@/utils/feedback-report-format';

import { SettingsSection } from '../../settings-section';

import type { SyncStatus } from '@littlebird/sdk';

const REFRESH_MS = 5000;

export function useSyncStatus(intervalMs = REFRESH_MS): SyncStatus | null {
  const [status, setStatus] = useState<SyncStatus | null>(null);
  useEffect(() => {
    let cancelled = false;
    const refresh = () =>
      littlebirdSdk
        .getSyncStatus()
        .then((next) => {
          if (!cancelled) setStatus(next);
        })
        .catch(() => {}); // keep the last good status on screen
    void refresh();
    const unlisten = littlebirdSdk.onBootstrap(() => void refresh());
    const timer = setInterval(() => void refresh(), intervalMs);
    return () => {
      cancelled = true;
      unlisten();
      clearInterval(timer);
    };
  }, [intervalMs]);
  return status;
}

export function SyncDiagnostics() {
  const status = useSyncStatus();
  const text = status
    ? formatSyncSection(status)
        .replace(/^\n+=== Sync ===\n/, '')
        .replace(/\n- Degraded: .*/g, '')
    : 'Loading…';
  return (
    <SettingsSection.Item searchId="support.sync-diagnostics">
      <SettingsSection.ItemHeader>
        <SettingsSection.ItemLabel>Sync diagnostics</SettingsSection.ItemLabel>
        <SettingsSection.ItemDescription>
          Where this device is relative to the server, and what is still waiting
          to upload.
        </SettingsSection.ItemDescription>
      </SettingsSection.ItemHeader>
      {status && status.degraded.length > 0 && (
        <p role="alert" className="text-destructive text-sm">
          {status.degraded.join('; ')}
        </p>
      )}
      <pre className="text-xs whitespace-pre-wrap font-mono">{text}</pre>
      <Button
        variant="outline"
        size="sm"
        onClick={() => void navigator.clipboard.writeText(text)}
      >
        Copy
      </Button>
    </SettingsSection.Item>
  );
}
